<template>
  <div ref="containerRef" class="book-search">
    <div class="form form--gray">
      <input
        type="text"
        v-model="searchQuery"
        @input="performSearch"
        @focus="handleFocus"
        :placeholder="placeholder"
        class=""
        autocomplete="off"
      />
      <button
        v-if="searchQuery"
        @click="clearSearch"
        class="book-search__clear"
        type="button"
        aria-label="Ryd søgning"
      >
        ×
      </button>
      <div v-if="isLoading" class="book-search__loading">Søger...</div>
    </div>

    <div v-if="showResults && hasResults" class="book-search__results">
      <div
        v-for="book in results"
        :key="book.id"
        class="book-search__result-item"
      >
        <a :href="book.permalink" class="book-search__result-link">
          <div v-if="book.thumbnail" class="book-search__result-thumbnail">
            <img :src="book.thumbnail" :alt="book.title" />
          </div>
          <div class="book-search__result-content">
            <h3 class="book-search__result-title">{{ book.title }}</h3>
            <div
              v-if="postType === 'book' && book.authors && book.authors.length > 0"
              class="book-search__result-authors"
            >
              Af {{ formatAuthors(book.authors) }}
            </div>
            <div v-if="postType === 'book' && book.category" class="book-search__result-category">
              {{ book.category }}
            </div>
          </div>
        </a>
      </div>
    </div>

    <div v-if="shouldShowResults" class="book-search__no-results">
      Ingen resultater fundet
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';

export default {
  name: 'BookSearch',
  props: {
    postType: {
      type: String,
      default: 'book',
      validator: (value) => ['book', 'authors'].includes(value),
    },
    placeholder: {
      type: String,
      default: 'Søg efter bøger...',
    },
    apiEndpoint: {
      type: String,
      default: '/wp-json/alpha/v1/search-books',
    },
  },
  setup(props) {
    const searchQuery = ref('');
    const results = ref([]);
    const isLoading = ref(false);
    const searchTimeout = ref(null);
    const showResults = ref(false);
    const containerRef = ref(null);

    const hasResults = computed(() => results.value.length > 0);
    const canSearch = computed(() => searchQuery.value.length >= 2);
    const shouldShowResults = computed(
      () =>
        showResults.value &&
        canSearch.value &&
        !isLoading.value &&
        !hasResults.value
    );

    const performSearch = () => {
      // Clear existing timeout
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }

      // If query is less than 2 characters, clear results
      if (searchQuery.value.length < 2) {
        results.value = [];
        showResults.value = false;
        return;
      }

      // Set loading state
      isLoading.value = true;
      showResults.value = true;

      // Set timeout for 300ms delay
      searchTimeout.value = setTimeout(() => {
        // WordPress REST API endpoint
        const restUrl =
          props.apiEndpoint + '?q=' +
          encodeURIComponent(searchQuery.value);

        fetch(restUrl)
          .then((response) => response.json())
          .then((data) => {
            if (data.success) {
              results.value = data.data || [];
            } else {
              results.value = [];
            }
            isLoading.value = false;
          })
          .catch((error) => {
            console.error('Search error:', error);
            results.value = [];
            isLoading.value = false;
          });
      }, 300);
    };

    const clearSearch = () => {
      searchQuery.value = '';
      results.value = [];
      showResults.value = false;
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
    };

    const formatAuthors = (authors) => {
      if (!authors || authors.length === 0) {
        return '';
      }
      return authors.join(' & ');
    };

    const handleFocus = () => {
      if (searchQuery.value.length >= 2 && results.value.length > 0) {
        showResults.value = true;
      }
    };

    const handleClickOutside = (e) => {
      if (containerRef.value && !containerRef.value.contains(e.target)) {
        showResults.value = false;
      }
    };

    onMounted(() => {
      // Close results when clicking outside
      document.addEventListener('click', handleClickOutside);
    });

    onUnmounted(() => {
      // Clean up event listener
      document.removeEventListener('click', handleClickOutside);
      // Clear timeout if component is unmounted
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
    });

    return {
      containerRef,
      searchQuery,
      results,
      isLoading,
      showResults,
      hasResults,
      canSearch,
      shouldShowResults,
      performSearch,
      clearSearch,
      formatAuthors,
      handleFocus,
      postType: props.postType,
      placeholder: props.placeholder,
    };
  },
};
</script>

<style scoped lang="scss">
input {
  &::placeholder {
    color: #666;
  }
}
.book-search {
  position: relative;
  width: 100%;
  max-width: 600px;

  book-search {
    position: relative;
    display: flex;
    align-items: center;
  }

  &__clear {
    position: absolute;
    right: 0.75rem;
    background: none;
    border: none;
    font-size: 1.5rem;
    line-height: 1;
    color: #999;
    cursor: pointer;
    padding: 0;
    width: 1.5rem;
    height: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.3s ease;

    &:hover {
      color: #333;
    }
  }

  &__loading {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    padding: 1rem;
    text-align: center;
    color: #666;
    font-size: 0.9rem;
    background: #fff;
    border: 2px solid #ddd;
    border-top: none;
    border-radius: 0 0 4px 4px;
  }

  &__results {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    max-height: 500px;
    overflow-y: auto;
    background: #fff;
    border: 2px solid #ddd;
    border-top: none;
    border-radius: 0 0 4px 4px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    z-index: 1000;
  }

  &__result-item {
    border-bottom: 1px solid #eee;

    &:last-child {
      border-bottom: none;
    }
  }

  &__result-link {
    display: flex;
    padding: 1rem;
    text-decoration: none;
    color: inherit;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: #f5f5f5;
    }
  }

  &__result-thumbnail {
    flex-shrink: 0;
    width: 60px;
    height: 60px;
    margin-right: 1rem;
    overflow: hidden;
    border-radius: 4px;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__result-content {
    flex: 1;
    min-width: 0;
  }

  &__result-title {
    margin: 0 0 0.25rem 0;
    font-size: 1rem;
    font-weight: 600;
    color: #333;
    line-height: 1.3;
  }

  &__result-authors {
    font-size: 0.875rem;
    color: #666;
    margin-bottom: 0.25rem;
  }

  &__result-category {
    font-size: 0.75rem;
    color: #999;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  &__no-results {
    padding: 1.5rem;
    text-align: center;
    color: #666;
    font-size: 0.9rem;
    background: #fff;
    border: 2px solid #ddd;
    border-top: none;
    border-radius: 0 0 4px 4px;
  }
}
</style>
